import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';

import Button from '../../../shared/_TBComponents/Button';
import Toggle from '../../../shared/_TBComponents/Toggle';
import { updateSetting } from '../ducks/leaderboardActions';

const ContentWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
`;

const Header = styled('div')`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 24px;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
  color: #444444;
`;

const Subtitle = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: #9e9e9e;
`;

const SettingsRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  .select__value-container {
    width: 130px;
    padding: 2px 16px 2px 12px;
  }
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-right: 30px;
`;

const SettingLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #444444;
`;

const SettingDescription = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  color: #9e9e9e;
`;

const buttonTextProps = { fontWeight: '600', letterSpacing: '0px' };

const BoardSettingsModal = ({
  closeModal,
  leaderboardId,
  boardNumber,
}) => {
  const leaderboardSettings = useSelector(
    (state) => state.leaderboard.data.leaderboards[leaderboardId].settings,
  );

  const dispatch = useDispatch();

  const { autoScroll, autoLoad, loadTime } = leaderboardSettings;

  const toggleAutoScroll = () => {
    dispatch(updateSetting(leaderboardId, 'autoScroll', !autoScroll));
  };

  const toggleAutoLoad = () => {
    dispatch(updateSetting(leaderboardId, 'autoLoad', !autoLoad));
  };

  const setLoadTime = (selectedTime) => {
    dispatch(updateSetting(leaderboardId, 'loadTime', selectedTime));
  };

  return (
    <ContentWrapper>
      <Header>
        <Title>Leaderboard Settings</Title>
        {boardNumber > 0 && <Subtitle>{`(Leaderboard ${boardNumber})`}</Subtitle>}
      </Header>
      <SettingsRow>
        <TextContainer>
          <SettingLabel>Scroll Leaderboard Automatically</SettingLabel>
          <SettingDescription>The leaderboard table will automatically scroll down.</SettingDescription>
        </TextContainer>
        <Toggle
          defaultChecked={autoScroll}
          onChange={toggleAutoScroll}
          width='auto'
        />
      </SettingsRow>
      <SettingsRow>
        <TextContainer>
          <SettingLabel>Reload Leaderboard Automatically</SettingLabel>
          <SettingDescription>
            The leaderboard table will reload user data after a specified amount of time.
          </SettingDescription>
        </TextContainer>
        <Toggle
          defaultChecked={autoLoad}
          onChange={toggleAutoLoad}
          width='auto'
        />
      </SettingsRow>
      <SettingsRow>
        <TextContainer>
          <SettingLabel>Time Before Reload</SettingLabel>
          <SettingDescription>
            Specified amount of time before leaderboard data is reloaded.
          </SettingDescription>
        </TextContainer>
        <Select
          classNamePrefix='select'
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
          onChange={(option) => setLoadTime(option)}
          options={[
            { value: 60000, label: 'One Minute' },
            { value: 300000, label: 'Five Minutes' },
            { value: 900000, label: 'Fifteen Minutes' },
            { value: 1800000, label: 'Thirty Minutes' },
          ]}
          value={loadTime}
        />
      </SettingsRow>
      <Button
        name='Close'
        bgColor='#444444'
        txtColor='white'
        noBorder
        fullWidth
        fontSize='14px'
        customBorderRadius='4px'
        styledTextProps={buttonTextProps}
        onClick={closeModal}
        type='button'
      />
    </ContentWrapper>
  );
};

BoardSettingsModal.defaultProps = {
  boardNumber: 0,
};

BoardSettingsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  leaderboardId: PropTypes.number.isRequired,
  boardNumber: PropTypes.number,
};

export default BoardSettingsModal;
